// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import { ContextMenuTrigger, ContextMenu, MenuItem } from 'react-contextmenu';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Icon from 'shared/components/common/Icon';
import Button from 'shared/components/common/Button';
import AddUpdateMachineTypeModal from 'main-app/components/AddUpdateMachineTypeModal';
import DeleteMachineTypeModal from 'main-app/components/DeleteMachineTypeModal';

type Props = {
  history: RouterHistory,
};

type State = {
  addModalOpen: boolean,
  updateModalRecord: ?Object,
  deleteModalRecord: ?Object,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
`;

const ButtonWrapper = styled.div`
  padding: 16px 16px 24px 16px;

  @media (min-width: 1000px) {
    align-self: flex-end;
    padding: 0 0 24px;
  }
`;

const ListWrapper = styled.div`
  flex: 1;
`;

class MachineTypes extends PureComponent<Props, State> {
  menuTriggerRefs = {};

  constructor(props: Props) {
    super(props);

    this.state = {
      addModalOpen: false,
      updateModalRecord: null,
      deleteModalRecord: null,
    };
  }

  handleOpenUpdateModal = (target: *, edges: ?Array<Object>) => {
    const id = target.getAttribute('machine-type-id');

    const record = edges ? edges.find(({ node }) => node.id === id) : null;

    if (record) {
      this.setState({
        updateModalRecord: record.node,
      });
    }
  };

  handleOpenDeleteModal = (target: *, edges: ?Array<Object>) => {
    const id = target.getAttribute('machine-type-id');

    const record = edges ? edges.find(({ node }) => node.id === id) : null;

    if (record) {
      this.setState({
        deleteModalRecord: record.node,
      });
    }
  };

  handleOpenCreateModal = () => {
    this.setState({
      addModalOpen: true,
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      addModalOpen: false,
      updateModalRecord: null,
      deleteModalRecord: false,
    });
  };

  render() {
    const { history } = this.props;
    const { addModalOpen, updateModalRecord, deleteModalRecord } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        query={graphql`
          query MachineTypesQuery {
            machineTypes(first: null)
              @connection(key: "MachineTypes_machineTypes", filters: []) {
              edges {
                node {
                  id
                  name
                  ...AddUpdateMachineTypeModal_machineType
                  ...DeleteMachineTypeModal_machineType
                }
              }
            }
          }
        `}
        variables={null}
        render={({ props }) => {
          if (!props) {
            return <Loader />;
          }

          return (
            <Wrapper>
              <ButtonWrapper>
                <Button
                  onClick={this.handleOpenCreateModal}
                  theme="border-white"
                >
                  {i18n.t('Create Work Center Type')}
                </Button>
              </ButtonWrapper>

              {!props.machineTypes.edges.length ? (
                <div>
                  {i18n.t(`You haven't added any work centers types yet.`)}
                </div>
              ) : (
                <ListWrapper>
                  <Table
                    data={props.machineTypes.edges.map(edge => edge.node)}
                    columns={[
                      {
                        Header: i18n.t('Work Center Type'),
                        accessor: 'name',
                      },
                      {
                        Header: '',
                        accessor: 'id',
                        width: 48,
                        enforceWidth: true,
                        Cell: ({ value }) => (
                          <ContextMenuTrigger
                            ref={r => (this.menuTriggerRefs[value] = r)}
                            id="settings-machine-types-menu"
                            attributes={{
                              'machine-type-id': value,
                            }}
                          >
                            <Icon
                              type="circle-context-menu"
                              size={24}
                              onClick={e => {
                                if (this.menuTriggerRefs[value]) {
                                  this.menuTriggerRefs[
                                    value
                                  ].handleContextClick(e);
                                }
                              }}
                            />
                          </ContextMenuTrigger>
                        ),
                      },
                    ]}
                  />
                </ListWrapper>
              )}
              {addModalOpen && (
                <AddUpdateMachineTypeModal
                  history={history}
                  onClose={this.handleCloseAllModals}
                  machineType={null}
                />
              )}
              {updateModalRecord && (
                <AddUpdateMachineTypeModal
                  history={history}
                  onClose={this.handleCloseAllModals}
                  machineType={updateModalRecord}
                />
              )}
              {deleteModalRecord && (
                <DeleteMachineTypeModal
                  onClose={this.handleCloseAllModals}
                  machineType={deleteModalRecord}
                />
              )}
              <ContextMenu id="settings-machine-types-menu">
                <MenuItem
                  onClick={(e, data, target) =>
                    this.handleOpenUpdateModal(target, props.machineTypes.edges)
                  }
                >
                  {i18n.t('Edit Work Center Type')}
                </MenuItem>
                <MenuItem
                  onClick={(e, data, target) =>
                    this.handleOpenDeleteModal(target, props.machineTypes.edges)
                  }
                >
                  {i18n.t('Delete Work Center Type')}
                </MenuItem>
              </ContextMenu>
            </Wrapper>
          );
        }}
      />
    );
  }
}

export default MachineTypes;
